import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

export default function DualActionFooter() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      {/* Decorative background shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-primary-50 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-amber-50 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="text-center mb-10 md:mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full mb-4 shadow-sm">
            <i className="ri-compass-3-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">What Is Your Goal?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4">
            Two Paths.
            <span className="text-primary-600"> One Partner.</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Whether you need a product built from scratch or a team to scale your engineering capacity — Thoughtic delivers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 max-w-5xl mx-auto">
          {/* Build Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7, delay: 0.15 }}
            className="group relative bg-gradient-to-br from-primary-50 to-white border border-primary-100 rounded-2xl p-6 md:p-8 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-500"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 to-primary-600 rounded-t-2xl"></div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-rocket-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-navy-900">Start a Project</h3>
                <p className="text-xs text-primary-600 font-semibold">Custom Software Development</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              From product discovery to deployed code. We design, engineer, and launch digital products for startups and enterprises — with full transparency and agile delivery.
            </p>

            <ul className="space-y-2.5 mb-6">
              {[
                'Free project roadmap & architecture review',
                'Dedicated cross-functional engineering squad',
                'Bi-weekly sprints with live progress dashboards',
                'Fixed-price or time-and-materials models',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-primary-50 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-primary-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/software-development"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group/link"
            >
              Get a Free Roadmap
              <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </motion.div>

          {/* Scale Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7, delay: 0.25 }}
            className="group relative bg-gradient-to-br from-amber-50 to-white border border-amber-100 rounded-2xl p-6 md:p-8 hover:border-amber-300 hover:shadow-xl hover:shadow-amber-500/10 transition-all duration-500"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-amber-600 rounded-t-2xl"></div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-user-add-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-navy-900">Hire Talent</h3>
                <p className="text-xs text-amber-700 font-semibold">IT Staff Augmentation</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Pre-vetted engineers matched to your stack and culture in 48 hours. Dedicated developers, contract staffing, offshore teams, and project-based squads.
            </p>

            <ul className="space-y-2.5 mb-6">
              {[
                '48-hour candidate shortlist guarantee',
                'Pre-vetted, interview-ready profiles',
                'Risk-free 14-day replacement policy',
                'Transparent hourly and monthly rates',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-amber-50 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-amber-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/talent-solutions"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group/link"
            >
              Request Talent Matches
              <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}